import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import type { ApiResponse } from '@/types';
import { costExportFilename, filenameFromContentDisposition } from '@/modules/analytics-v2/cost-format';
import type {
  AnalyticsV2CategoriesQueryParams,
  AnalyticsV2QueryParams,
  AnalyticsV2SeriesQueryParams,
  CategoryTableRow,
  CostBreakdown,
  CostBreakdownGroupBy,
  CostExportType,
  CostQueryParams,
  CostTicketTypes,
  CostFeatures,
  CostGranularity,
  CostSeries,
  CostSeriesGroupBy,
  CostSummary,
  CostAccess,
  CostTicketDetail,
  CostTickets,
  CostViewer,
  CostViewerList,
  UsagePrice,
  UsagePriceInput,
  UsagePriceList,
  CostWeekly,
  CostWeeklyLevel,
  CoverageFunnelResult,
  MetricBreakdownRow,
  ScalarMetricKey,
  ScalarMetricResult,
  SeriesMetricKey,
  SeriesMetricResult
} from '@/types/analyticsV2';

/**
 * Brand scope on the wire.
 *
 * `buId`, camelCase, because that is the name kim-analytics' schema declares. Everywhere
 * else in the platform the wire name is `bu_id`, and the two proxies in front of
 * kim-analytics forward whatever they are given, so the spelling has to be right here.
 *
 * It fails loudly if it is not: every analytics-v2 querystring schema is
 * `additionalProperties: false`, so `bu_id` reaches kim-analytics and 400s as an unknown
 * parameter rather than being ignored. That also means the accepted param set is fixed end
 * to end — anything new added along this chain has to be declared in that schema too.
 *
 * Omitted rather than sent empty when unscoped: kim-analytics reads a missing `buId` as
 * the whole account, while `buId=` fails its positive-integer pattern.
 */
const buScopeParam = (buId: string | null | undefined): { buId?: string } =>
  buId ? { buId } : {};

/** GET through the relay and unwrap the ApiResponse envelope. */
const getData = async <T>(url: string, params: Record<string, unknown>): Promise<T> =>
  (await api.get<ApiResponse<T>>(url, { params })).data.data;

const rangeParams = (params: AnalyticsV2QueryParams) => ({
  fromDate: params.fromDate,
  toDate: params.toDate,
  accountId: params.accountId,
  ...buScopeParam(params.buId)
});

/**
 * The metric endpoints (/metrics, /series, /breakdown): range + brand + `filters`, sent as one JSON
 * param because the relays forward each param as one string. Only these endpoints declare `filters`.
 */
const metricParams = (params: AnalyticsV2QueryParams) => ({
  ...rangeParams(params),
  ...(params.filters && Object.keys(params.filters).length > 0 ? { filters: JSON.stringify(params.filters) } : {})
});

/** Cost endpoints: range + brand + whichever ticket filters are set (unset ones are omitted, not sent empty). */
const costParams = (params: CostQueryParams) => ({
  ...rangeParams(params),
  ...(params.channel ? { channel: params.channel } : {}),
  ...(params.category ? { category: params.category } : {}),
  ...(params.intent ? { intent: params.intent } : {})
});

// Reads go through machine's /admin/analytics relay to kim-analytics. Both that relay and
// kim-ai-backend behind it forward the query string generically, so `buId` needs no change
// in either.
export const analyticsV2Service = {
  /** Fetches scalar metrics in one call, keyed by metric key. */
  async getMetrics<K extends string = ScalarMetricKey>(
    keys: readonly K[],
    params: AnalyticsV2QueryParams
  ): Promise<Record<K, ScalarMetricResult>> {
    return getData(API_ENDPOINTS.ANALYTICS.METRICS, { keys: keys.join(','), ...metricParams(params) });
  },

  /** Same as getMetrics, but each series is bucketed by granularity. */
  async getSeries<K extends string = SeriesMetricKey>(
    keys: readonly K[],
    params: AnalyticsV2SeriesQueryParams
  ): Promise<Record<K, SeriesMetricResult>> {
    return getData(API_ENDPOINTS.ANALYTICS.METRICS_SERIES, {
      keys: keys.join(','),
      granularity: params.granularity,
      ...metricParams(params)
    });
  },

  /** The metrics per group of `groupBy`, e.g. ['category', 'subcategory'], for the tables. */
  async getBreakdown<D extends string, K extends string>(
    keys: readonly K[],
    groupBy: readonly D[],
    params: AnalyticsV2QueryParams
  ): Promise<MetricBreakdownRow<D, K>[]> {
    const data = await getData<{ rows: MetricBreakdownRow<D, K>[] }>(API_ENDPOINTS.ANALYTICS.METRICS_BREAKDOWN, {
      keys: keys.join(','),
      groupBy: groupBy.join(','),
      ...metricParams(params)
    });
    return data.rows;
  },

  /** One row per category for the Ticket Metrics table. */
  async getCategories(
    params: AnalyticsV2CategoriesQueryParams
  ): Promise<CategoryTableRow[]> {
    // kim-analytics f6b9372 renamed the row fields (key, aiHandled, avgAiResponses).
    const res = await api.get<
      ApiResponse<
        {
          key: string | null;
          tickets: number;
          aiHandled: CategoryTableRow['withAi'];
          automated: CategoryTableRow['automated'];
          avgAiResponses: number | null;
        }[]
      >
    >(
      API_ENDPOINTS.ANALYTICS.CATEGORIES,
      {
        params: {
          fromDate: params.fromDate,
          toDate: params.toDate,
          ...(params.ticketCategory
            ? { ticketCategory: params.ticketCategory }
            : {}),
          accountId: params.accountId,
          ...buScopeParam(params.buId)
        }
      }
    );
    return res.data.data.map((r) => ({
      category: r.key ?? 'Uncategorized',
      tickets: r.tickets,
      avgAiResponsesPerTicket: r.avgAiResponses,
      withAi: r.aiHandled,
      automated: r.automated,
      ticketCategory: null
    }));
  },

  /** Coverage funnel stages plus the HITL approved/rejected/pending split. */
  async getFunnel(
    params: AnalyticsV2QueryParams
  ): Promise<CoverageFunnelResult> {
    const res = await api.get<ApiResponse<CoverageFunnelResult>>(
      API_ENDPOINTS.ANALYTICS.FUNNEL,
      {
        params: {
          fromDate: params.fromDate,
          toDate: params.toDate,
          accountId: params.accountId,
          ...buScopeParam(params.buId)
        }
      }
    );
    return res.data.data;
  },

  // ─── AI cost ledger (kim-analytics /api/analytics-v2/cost/*) ───────────────

  getCostSummary(params: CostQueryParams): Promise<CostSummary> {
    return getData(API_ENDPOINTS.ANALYTICS.COST_SUMMARY, costParams(params));
  },

  getCostSeries(
    params: CostQueryParams,
    granularity: CostGranularity,
    groupBy: CostSeriesGroupBy
  ): Promise<CostSeries> {
    return getData(API_ENDPOINTS.ANALYTICS.COST_SERIES, { ...costParams(params), granularity, groupBy });
  },

  getCostBreakdown(params: CostQueryParams, groupBy: CostBreakdownGroupBy): Promise<CostBreakdown> {
    return getData(API_ENDPOINTS.ANALYTICS.COST_BREAKDOWN, { ...costParams(params), groupBy });
  },

  /** Spend by feature area, each with its raw features. */
  getCostFeatures(params: CostQueryParams): Promise<CostFeatures> {
    return getData(API_ENDPOINTS.ANALYTICS.COST_FEATURES, costParams(params));
  },

  /** Brand × week report. */
  getCostWeekly(params: CostQueryParams, level: CostWeeklyLevel): Promise<CostWeekly> {
    return getData(API_ENDPOINTS.ANALYTICS.COST_WEEKLY, { ...costParams(params), level });
  },

  /** Most expensive tickets first. kim-analytics caps `limit` at 200. */
  getCostTickets(params: CostQueryParams, limit: number, offset: number): Promise<CostTickets> {
    // kim-analytics declares limit/offset as digit strings; axios serialises numbers the same way.
    return getData(API_ENDPOINTS.ANALYTICS.COST_TICKETS, { ...costParams(params), limit, offset });
  },

  /** Channels and intent categories of the range's tickets, for the filter pickers. Range + brand only. */
  getCostTicketTypes(params: AnalyticsV2QueryParams): Promise<CostTicketTypes> {
    return getData(API_ENDPOINTS.ANALYTICS.COST_TICKET_TYPES, rangeParams(params));
  },

  /** Every call of one conversation, grouped by response. 404s when it has no usage in this account. */
  getCostTicket(params: { accountId: number; buId?: string | null; conversationId: number }): Promise<CostTicketDetail> {
    return getData(API_ENDPOINTS.ANALYTICS.COST_TICKET, {
      conversationId: params.conversationId,
      accountId: params.accountId,
      ...buScopeParam(params.buId)
    });
  },

  /** CSV download. The filename comes from Content-Disposition, else the same pattern kim-analytics uses. */
  async downloadCostExport(
    params: CostQueryParams,
    type: CostExportType,
    /** Weekly only; other types reject unknown params. */
    level?: CostWeeklyLevel
  ): Promise<{ blob: Blob; filename: string }> {
    const res = await api.get<Blob>(API_ENDPOINTS.ANALYTICS.COST_EXPORT, {
      params: { ...costParams(params), type, ...(type === 'weekly' && level ? { level } : {}) },
      responseType: 'blob'
    });
    const header = res.headers?.['content-disposition'];
    const filename =
      filenameFromContentDisposition(typeof header === 'string' ? header : null) ?? costExportFilename(type, params);
    return { blob: res.data, filename };
  },

  // ─── AI cost allowlist (kim-ai-backend /api/analytics/cost-viewers) ───────

  /** Whether the signed-in user may see AI cost data. Answers for anyone signed in. */
  getCostAccess(): Promise<CostAccess> {
    return getData(API_ENDPOINTS.ANALYTICS.COST_VIEWERS_ME, {});
  },

  /** 403s unless the caller is on the list. */
  listCostViewers(): Promise<CostViewerList> {
    return getData(API_ENDPOINTS.ANALYTICS.COST_VIEWERS, {});
  },

  async addCostViewer(email: string): Promise<CostViewer> {
    return (await api.post<ApiResponse<CostViewer>>(API_ENDPOINTS.ANALYTICS.COST_VIEWERS, { email })).data.data;
  },

  /** 400s when it is the last viewer. */
  async removeCostViewer(email: string): Promise<void> {
    await api.delete(API_ENDPOINTS.ANALYTICS.COST_VIEWERS, { params: { email } });
  },

  // ─── AI prices (kim-ai-backend /api/analytics/cost-prices, same allowlist) ───

  listCostPrices(): Promise<UsagePriceList> {
    return getData(API_ENDPOINTS.ANALYTICS.COST_PRICES, {});
  },

  /** 400 with the backend's reason when a value is out of range. */
  async saveCostPrice(input: UsagePriceInput): Promise<UsagePrice> {
    return (await api.post<ApiResponse<UsagePrice>>(API_ENDPOINTS.ANALYTICS.COST_PRICES, input)).data.data;
  }
};
